/* MobilePaymentDialog — compact, touch-first fiscal close for waiters.

   Why a dedicated mobile component instead of reusing PaymentDialogBody
   from POSRetail.jsx: the desktop dialog is 320 px of always-visible
   numpad + multi-row method grid + side-by-side action buttons, which
   spills off-screen on a 390 px viewport and forces the operator to
   scroll past buttons that should be one tap away.

   Layout (top → bottom):
     ┌── tappable Σύνολο card ─────────────────────────────────────┐
     │  Press to flip to ΡΕΣΤΑ / Ληφθέντα Μετρητά view.            │
     ├── payment methods (2-col grid) ─────────────────────────────┤
     ├── action row: «Εκτύπωση / Παραγγελίας» (wraps), «Μερική    │
     │   Είσπραξη» — both 12-tall, 2-line labels                  │
     └── primary CTA: ΕΚΔΟΣΗ ΑΠΟΔΕΙΞΗΣ ────────────────────────────┘

   The numpad is rendered inside a SECONDARY modal (NumpadSheet) that
   only opens when the operator taps the «Ληφθέντα Μετρητά» card —
   keeping the main dialog short enough to fit on a phone screen.

   Props mirror PaymentDialogBody so the same close/print/partial
   handlers from MobileRestaurant.jsx can be wired with zero changes.
*/
import { useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Receipt, Printer, X, Delete, Wallet, Split } from "lucide-react";
import { fmtEUR } from "@/lib/api";

export default function MobilePaymentDialog({
  total,
  payments,
  split,
  setSplit,
  busy,
  cartEmpty,
  onPrintOrder,
  onSubmit,
  onPartialOpen,
  hidePartialButton = false,
  // When set, the operator is paying for un-fiscalized items — non-cash
  // methods are hidden so they can't be picked by accident. Optional
  // ``breakdown`` ({fiscalSum, cashOnlySum, fiscalCount, cashOnlyCount})
  // renders an explanatory banner above the methods grid.
  cashOnly = false,
  breakdown = null,
}) {
  // Identify cash by name first, falling back to payment_id=1 (legacy
  // convention used by Restaurant.jsx / POSRetail.jsx).
  const cash = useMemo(
    () =>
      payments.find((p) => /ΜΕΤΡΗΤ/i.test(p.name || "")) ||
      payments.find((p) => p.payment_id === 1) ||
      payments[0],
    [payments],
  );
  // In cashOnly mode the only allowed method is cash — filter eagerly
  // so the operator can't even see (let alone tap) a card button. If a
  // deployment somehow has no method named ΜΕΤΡΗΤΑ, we must NOT silently
  // fall through to all methods (that would defeat the safety contract);
  // instead the parent should refuse to submit — but at least keep the
  // grid empty so the operator notices something is wrong.
  const visiblePayments = useMemo(() => {
    if (!cashOnly) return payments;
    return cash ? [cash] : [];
  }, [cashOnly, cash, payments]);
  const [activePid, setActivePid] = useState(null);
  const [received, setReceived] = useState("");
  // Two independent panels — kept separate so opening one doesn't
  // clobber the other's animation state.
  const [numpadOpen, setNumpadOpen] = useState(false);
  const [changeOpen, setChangeOpen] = useState(false);

  // Initial setup: default to Cash so the operator has one less tap.
  // In cashOnly mode the cash method is also enforced (any pre-existing
  // non-cash selection from a sibling flow is overwritten).
  useEffect(() => {
    if (cash && (activePid === null || (cashOnly && activePid !== cash.payment_id))) {
      setActivePid(cash.payment_id);
    }
  }, [cash, activePid, cashOnly]);

  // In cashOnly mode the «Ληφθέντα Μετρητά» field is pre-filled with the
  // exact total so the operator just taps Confirm. Without this the
  // partial-payment payload would send {amount: null} and the backend
  // refuses to settle. Operator can still edit via numpad if needed.
  useEffect(() => {
    if (cashOnly && total > 0 && received === "") {
      setReceived(total.toFixed(2));
    }
  }, [cashOnly, total, received]);

  // Propagate the current selection into the parent's split[] so
  // closeTableNow can ship it via /ftext/restaurant-close.
  useEffect(() => {
    if (!activePid) { setSplit([]); return; }
    const amt = parseFloat(String(received).replace(",", "."));
    const isCash = cash && activePid === cash.payment_id;
    setSplit([{
      payment_id: activePid,
      amount: isCash && Number.isFinite(amt) ? String(amt) : "",
    }]);
  }, [activePid, received, cash, setSplit]);

  const numericReceived = parseFloat(String(received).replace(",", ".")) || 0;
  const isCashActive = cash && activePid === cash.payment_id;
  const change = isCashActive && numericReceived > 0 ? numericReceived - total : 0;
  // 2026-02: Ίδιο guard με το desktop Retail — απαγορεύουμε submit όταν
  // ο ταμίας πληκτρολογήσει «Ληφθέντα» μικρότερα από το σύνολο. Κενό
  // input επιτρέπεται (implicit «ακριβώς το ποσό»).
  const cashInsufficient = isCashActive && numericReceived > 0 && numericReceived + 0.005 < total;

  const pressKey = (k) => {
    setReceived((v) => {
      if (k === "back") return v.slice(0, -1);
      if (k === "clear") return "";
      if (k === ",") return v.includes(".") ? v : v.length ? v + "." : "0.";
      return v + k;
    });
  };
  const addQuick = (amount) => {
    const current = parseFloat(String(received).replace(",", ".")) || 0;
    setReceived((current + amount).toFixed(2));
  };

  // We disable the «Σύνολο» tap when the active method isn't cash —
  // the change/numpad concept doesn't apply to card payments.
  const totalCardClickable = !!isCashActive;

  return (
    <div className="flex flex-col gap-3" data-testid="mobile-payment-dialog">
      {/* CASH-ONLY breakdown banner — shown only in mixed-payment flow
          where some selected items have not been sent to the cash
          register yet. The operator must collect cash for them. */}
      {cashOnly && breakdown && (breakdown.fiscalCount + breakdown.cashOnlyCount) > 0 && (
        <div
          className="rounded-xl border-2 border-amber-400 bg-amber-50 p-2.5 text-xs leading-snug"
          data-testid="mobile-payment-breakdown"
        >
          <div className="font-bold text-amber-900 mb-1 flex items-center gap-1">
            ⚠ Διαχωρισμός Είσπραξης
          </div>
          {breakdown.fiscalCount > 0 && (
            <div className="flex items-center justify-between text-emerald-900" data-testid="mobile-payment-breakdown-fiscal">
              <span>{breakdown.fiscalCount} είδ. φορολογικά (κάρτα/μετρητά)</span>
              <span className="font-mono font-bold">{fmtEUR(breakdown.fiscalSum)}</span>
            </div>
          )}
          {breakdown.cashOnlyCount > 0 && (
            <div className="flex items-center justify-between text-amber-900 font-semibold" data-testid="mobile-payment-breakdown-cash-only">
              <span>{breakdown.cashOnlyCount} είδ. ΜΟΝΟ ΜΕΤΡΗΤΑ</span>
              <span className="font-mono font-extrabold">{fmtEUR(breakdown.cashOnlySum)}</span>
            </div>
          )}
        </div>
      )}

      {/* Σύνολο card — tappable to expose ρέστα panel */}
      <button
        type="button"
        onClick={() => totalCardClickable && setChangeOpen(true)}
        disabled={!totalCardClickable}
        className={`rounded-xl text-white px-4 py-3 flex items-baseline justify-between
                    ${totalCardClickable ? "bg-slate-900 active:bg-slate-800" : "bg-slate-700"}`}
        data-testid="mobile-payment-total-card"
      >
        <span className="text-[11px] uppercase tracking-wider text-slate-300">Σύνολο</span>
        <span className="font-mono tabular text-3xl font-extrabold text-emerald-400">
          {fmtEUR(total)}
        </span>
      </button>

      {/* Cash received (collapsed → tap to open numpad sheet) */}
      {isCashActive && (
        <button
          type="button"
          onClick={() => setNumpadOpen(true)}
          className="rounded-xl border-2 border-emerald-300 bg-emerald-50/40 px-3 py-2 flex items-center justify-between active:bg-emerald-100/60"
          data-testid="mobile-payment-received-open"
        >
          <div className="text-left">
            <div className="text-[10px] uppercase tracking-wider font-bold text-slate-600">Ληφθέντα Μετρητά</div>
            <div className="font-mono tabular text-xl font-extrabold text-slate-900">
              {received ? fmtEUR(numericReceived) : "—"}
            </div>
          </div>
          {numericReceived > 0 && (
            <div className="text-right">
              <div className="text-[10px] uppercase tracking-wider text-slate-500">
                {change >= 0 ? "Ρέστα" : "Λείπουν"}
              </div>
              <div className={`font-mono tabular text-xl font-extrabold ${change >= 0 ? "text-emerald-700" : "text-red-600"}`}>
                {fmtEUR(change >= 0 ? change : -change)}
              </div>
            </div>
          )}
          <Wallet size={20} className="text-emerald-700 shrink-0 ml-2" />
        </button>
      )}

      {/* Payment methods — 2-col grid */}
      <div>
        <div className="text-[11px] uppercase tracking-wider text-slate-500 font-bold mb-1.5 flex items-center justify-between">
          <span>Τρόπος Πληρωμής</span>
          {cashOnly && (
            <span className="text-amber-700 font-extrabold" data-testid="mobile-payment-cash-only-tag">
              ΜΟΝΟ ΜΕΤΡΗΤΑ
            </span>
          )}
        </div>
        <div className="grid grid-cols-2 gap-2">
          {visiblePayments.map((p) => {
            const active = activePid === p.payment_id;
            return (
              <button
                key={p.id}
                type="button"
                onClick={() => setActivePid(p.payment_id)}
                data-testid={`pay-method-${p.payment_id}`}
                className={`rounded-lg border-2 py-2.5 px-2 text-xs font-bold transition-all active:scale-[0.97] ${
                  active
                    ? "bg-emerald-600 text-white border-emerald-700 shadow-md"
                    : "bg-white border-slate-300 text-slate-800"
                }`}
              >
                {p.name}
              </button>
            );
          })}
        </div>
      </div>

      {/* Action row — print order (2-line label) + partial payment.
          The partial button is hidden when this dialog is nested inside
          the partial-payment flow (otherwise the operator could open a
          partial-of-partial which the backend doesn't support). */}
      <div className={hidePartialButton ? "grid grid-cols-1 gap-2" : "grid grid-cols-2 gap-2"}>
        <Button
          onClick={onPrintOrder}
          disabled={busy || cartEmpty}
          variant="outline"
          className="h-12 border-2 border-blue-400 text-blue-700 font-bold text-xs leading-tight whitespace-normal py-1 flex flex-col items-center justify-center gap-0"
          data-testid="mobile-print-order-btn"
        >
          <Printer size={16} />
          <span className="block">Εκτύπωση<br/>Παραγγελίας</span>
        </Button>
        {!hidePartialButton && (
          <Button
            onClick={onPartialOpen}
            disabled={busy}
            variant="outline"
            className="h-12 border-2 border-amber-400 text-amber-700 font-bold text-xs leading-tight whitespace-normal py-1 flex flex-col items-center justify-center gap-0"
            data-testid="mobile-partial-payment-btn"
          >
            <Split size={16} />
            <span className="block">Μερική<br/>Είσπραξη</span>
          </Button>
        )}
      </div>

      {/* PRIMARY CTA */}
      <Button
        onClick={onSubmit}
        disabled={busy || cartEmpty || cashInsufficient}
        className="w-full bg-emerald-600 hover:bg-emerald-700 text-white h-14 font-bold text-lg shadow-md disabled:bg-slate-400 disabled:cursor-not-allowed"
        data-testid="confirm-payment-btn"
        title={cashInsufficient ? "Τα ληφθέντα μετρητά είναι μικρότερα από το σύνολο" : ""}
      >
        <Receipt size={22} className="mr-2" />
        {busy ? "Εκτύπωση..." : cashInsufficient ? "Ανεπαρκή Μετρητά" : "Έκδοση Απόδειξης"}
      </Button>

      {/* ─────────────── Secondary modal: Numpad ─────────────── */}
      <Dialog open={numpadOpen} onOpenChange={setNumpadOpen}>
        <DialogContent className="max-w-sm p-0 gap-0" data-testid="mobile-payment-numpad-dialog">
          <DialogHeader className="px-4 py-2 border-b">
            <DialogTitle className="text-sm flex items-center justify-between">
              <span>Καταχώρηση Ποσού</span>
              <button onClick={() => setNumpadOpen(false)} aria-label="Κλείσιμο">
                <X size={18} />
              </button>
            </DialogTitle>
          </DialogHeader>
          <div className="p-3 space-y-2">
            {/* Echo: input + change/missing */}
            <div className="rounded-lg border-2 border-emerald-300 bg-emerald-50/40 p-2 text-right">
              <div className="text-[10px] uppercase tracking-wider text-slate-500">Ληφθέντα</div>
              <div className="font-mono tabular text-2xl font-extrabold text-slate-900" data-testid="mobile-numpad-received">
                {received || "0"}
              </div>
              {numericReceived > 0 && (
                <div className={`text-xs font-bold ${change >= 0 ? "text-emerald-700" : "text-red-600"}`}>
                  {change >= 0 ? "Ρέστα" : "Λείπει"}: {fmtEUR(Math.abs(change))}
                </div>
              )}
            </div>

            {/* Quick-add row */}
            <div className="grid grid-cols-5 gap-1.5">
              <button
                type="button"
                onClick={() => setReceived(total.toFixed(2))}
                className="h-9 rounded-md border-2 border-blue-300 bg-blue-50 text-blue-800 font-bold text-xs active:scale-[0.97]"
                data-testid="mobile-quick-exact"
              >ΑΚΡ</button>
              {[5, 10, 20, 50].map((v) => (
                <button
                  key={v}
                  type="button"
                  onClick={() => addQuick(v)}
                  className="h-9 rounded-md border-2 border-slate-300 bg-white font-bold font-mono text-xs active:scale-[0.97]"
                  data-testid={`mobile-quick-${v}`}
                >+{v}</button>
              ))}
            </div>

            {/* Numpad */}
            <div className="grid grid-cols-3 gap-1.5">
              {["7","8","9","4","5","6","1","2","3",",","0","back"].map((k) => (
                <button
                  key={k}
                  type="button"
                  onClick={() => pressKey(k)}
                  data-testid={`mobile-pay-numpad-${k}`}
                  className="h-14 rounded-md bg-white border-2 border-slate-200 text-xl font-bold font-mono active:bg-slate-100"
                >
                  {k === "back" ? <Delete size={18} className="mx-auto" /> : k}
                </button>
              ))}
            </div>
            <button
              type="button"
              onClick={() => pressKey("clear")}
              className="w-full h-10 rounded-md bg-red-600 text-white text-sm font-extrabold active:bg-red-700"
              data-testid="mobile-numpad-clear"
            >C — Καθαρισμός</button>
            <Button
              onClick={() => setNumpadOpen(false)}
              className="w-full h-11 bg-emerald-600 hover:bg-emerald-700 text-white font-bold"
              data-testid="mobile-numpad-done"
            >Εντάξει</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* ─────────────── Secondary modal: Ρέστα ─────────────── */}
      <Dialog open={changeOpen} onOpenChange={setChangeOpen}>
        <DialogContent className="max-w-xs p-0 gap-0" data-testid="mobile-change-dialog">
          <DialogHeader className="px-4 py-3 border-b">
            <DialogTitle className="text-sm">Ρέστα</DialogTitle>
          </DialogHeader>
          <div className="p-4 space-y-2 text-center">
            <div>
              <div className="text-[11px] uppercase tracking-wider text-slate-500">Σύνολο Λογαριασμού</div>
              <div className="font-mono tabular text-2xl font-extrabold">{fmtEUR(total)}</div>
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-wider text-slate-500">Ληφθέντα</div>
              <div className="font-mono tabular text-2xl font-extrabold text-blue-700">{fmtEUR(numericReceived)}</div>
            </div>
            <div className="border-t pt-2">
              <div className="text-[11px] uppercase tracking-wider text-slate-500">Ρέστα προς πελάτη</div>
              <div className={`font-mono tabular text-3xl font-extrabold ${change >= 0 ? "text-emerald-700" : "text-red-600"}`}>
                {fmtEUR(Math.abs(change))}
              </div>
              {change < 0 && <div className="text-[11px] text-red-600">(λείπει — ζητήστε επιπλέον)</div>}
            </div>
            <Button
              onClick={() => setChangeOpen(false)}
              className="w-full mt-2 bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="mobile-change-close"
            >Κλείσιμο</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
